import styled from '@emotion/styled';
import type { FieldValue } from '@kizen/api';
import { Panel, VerticalTile } from '@kizen/kds/Tile';
import { StaticContentBlock } from '@kizen/page-builder/viewer';
import { useQuery } from '@tanstack/react-query';
import Color from 'color';
import {
  CSSProperties,
  Fragment,
  PropsWithChildren,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useBreakpoint } from 'app/spacing';
import { camelToSnakeCaseKeys } from 'services/helpers';
import {
  publicBusinessConfigQuery,
  selectPhoneRegionOptions,
} from 'query/business';
import { countriesQuery as getCountriesQuery } from 'query/geography';
import { displayNamesQuery, entityRecordQuery } from 'query/records';
import {
  type FieldValueWithRelations,
  type RelationshipValueWithRelation,
  getBusinessMergeFieldLookup,
  getBusinessVariablesMergeFieldLookup,
  getMergeFieldLookupForFields,
  getTeamMemberMergeFieldLookup,
  replaceMergeFields,
} from 'utility/merge-fields';
import { withErrorBoundary } from '../ErrorBoundary';
import ScrollFadeContainer from '__components/Kizen/ScrollFadeContainer';
import Loader from '__components/Kizen/Loader';
import { toastVariant, useToast } from '__components/ToastProvider';
import { useTranslation } from 'react-i18next';
import nodes from './nodes';

import TableScrollContainer from '__components/Tables/ScrollContainer';
import useExpandingVerticalScrollbar from 'hooks/useExpandingVericalScrollbar';
import { useRecordLayoutLogActivityContext } from 'ts-components/RecordLayout/RecordLayoutLogActivityContext';
import { useRecordLayoutStartAutomationContext } from 'ts-components/RecordLayout/RecordLayoutStartAutomationContext';

const ScrollWrapper = styled.div`
  overflow: auto;
  height: 100%;
  width: 100%;
  border-radius: 8px;
`;

const StyledScrollContainer = styled(TableScrollContainer)`
  flex-grow: 1;
  flex-shrink: 1;
  width: 100%;
  overflow-y: auto;
  height: 100%;

  & > div,
  & .ScrollChildren,
  & .ScrollChildren > div:first-child {
    width: 100%;
  }

  & .ScrollChildren > div:last-child {
    margin-left: -8px;
    right: 0;
    z-index: 16;
  }

  &
    .gradient-vertical-container
    .ScrollChildren
    .gradient-vertical-container
    > div:first-child
    > div {
    z-index: 10;
    pointer-events: none;
  }

  &
    .gradient-vertical-container
    .ScrollChildren
    .gradient-vertical-container
    > div:first-child {
    z-index: 10;
    pointer-events: none;
  }
`;

interface CustomContentStyle extends CSSProperties {
  '--custom-content-block-height'?: string;
}

type Scrolled = [boolean, boolean, boolean, boolean];

type RelationSnakeCase = RelationshipValueWithRelation['relation'];

type Relation = {
  /** Format: uuid */
  relatedField: string;
  /** Format: uuid */
  relatedObject: string;
  /** Format: uuid */
  relatedCategory: string | null;
  relatedName?: string | null;
  relatedObjectName: string;
  relatedEntityName: string;
  relation_type?:
    | 'one_to_one'
    | 'primary'
    | 'additional'
    | 'primary_for'
    | 'additional_for';
  cardinality: 'one_to_one' | 'many_to_many' | 'many_to_one' | 'one_to_many';
  fetchUrl: string;
  rollupTimeline?: boolean;
  rollupLeadsources?: boolean;
  inverseRelationRollupTimeline?: boolean;
  inverseRelationRollupLeadsources?: boolean;
  relatedObjectDefaultOnActivities: string;
};

type FieldLike = {
  displayName: string;
  fieldType: string;
  id: string;
  isDefault: boolean;
  meta: Record<string, any>;
  name: string;
  originalSnakeCaseName: string;
  relation?: Relation;
  options?: any[];
};

type CustomContnetBlockProps = {
  blockJson: any;
  fields: FieldLike[];
  id: string;
  isClient: boolean;
  javascriptActions: {
    id: string;
    name: string;
    script: string;
  }[];
  objectId: string;
  objectName: string;
  objectType: 'pipeline' | 'standard';
};

type StaticBlockWrapperProps = PropsWithChildren<{
  height: number;
  isMobile: boolean;
}>;

const StaticBlockWrapper = ({
  children,
  isMobile,
  height,
}: StaticBlockWrapperProps) => {
  const { containerProps } = useExpandingVerticalScrollbar();
  const [scrolled, setScrolled] = useState<Scrolled>([true, true, true, true]);

  if (isMobile) {
    return <Fragment>{children}</Fragment>;
  }

  return (
    <ScrollWrapper style={{ height: `${height}px` }}>
      <StyledScrollContainer
        {...containerProps}
        className="rounded-lg"
        onChangeScrolled={(scroll: Scrolled) => {
          setScrolled(scroll);
        }}
      >
        {height < 105 ? (
          children
        ) : (
          <ScrollFadeContainer top bottom scrolled={scrolled}>
            {children}
          </ScrollFadeContainer>
        )}
      </StyledScrollContainer>
    </ScrollWrapper>
  );
};

const Block = (props: CustomContnetBlockProps) => {
  const { blockJson, fields, id, javascriptActions, objectId, objectName } =
    props;
  const auth = useSelector((state: any) => state.authentication);
  const [showToast] = useToast();
  const { t } = useTranslation();
  const isMobile = useBreakpoint();
  const { openLogActivityModal } = useRecordLayoutLogActivityContext();
  const { startAutomation } = useRecordLayoutStartAutomationContext();

  const { isTransparent, style } = useMemo<{
    isTransparent: boolean;
    style: CustomContentStyle;
  }>(() => {
    if (!blockJson) {
      return { isTransparent: false, style: {} };
    }

    const background = Color(blockJson.ROOT.props.backgroundColor).object();
    const height = blockJson.ROOT.props.height;

    return {
      style: { '--custom-content-block-height': `${height}px` },
      isTransparent: background.alpha === 0,
    };
  }, [blockJson]);

  const rolesQuery = useQuery(
    displayNamesQuery(
      { role_ids: auth.teamMember.roles },
      {
        select: (res) => {
          return res.results.roles.map((role) => role.display_name);
        },
      }
    )
  );

  const countriesQuery = useQuery(getCountriesQuery());
  const phoneRegionsQuery = useQuery(
    publicBusinessConfigQuery({
      select: selectPhoneRegionOptions,
    })
  );

  const mergeFieldNames = useMemo(() => {
    const textContent: string[] = Object.values(blockJson)
      .filter((node: any) => node.type.resolvedName === 'Text')
      .map((node: any) => node.custom.text);
    const ids = textContent.reduce((acc, text) => {
      const matches = text.matchAll(
        new RegExp(`{{\\s*${objectName}\\.([A-Za-z0-9_]*)\\s*}}`, 'g') // {{ object_name.field_name }}
      );
      for (const match of matches) {
        if (match[1]) {
          acc.add(match[1]);
        }
      }
      return acc;
    }, new Set<string>());

    return [...ids];
  }, [objectName, blockJson]);

  const recordFieldsQuery = useQuery(
    entityRecordQuery(objectId, id, {
      params: {
        field_names: String(mergeFieldNames),
      },
      select: (res) => {
        return [...Object.values(res.fields)];
      },
    })
  );

  const isLoading =
    rolesQuery.isLoading ||
    countriesQuery.isLoading ||
    phoneRegionsQuery.isLoading ||
    recordFieldsQuery.isLoading;

  const textTransform = useMemo(() => {
    if (isLoading) {
      return null;
    }

    const relations = fields.reduce<Record<string, RelationSnakeCase>>(
      (acc, field) => {
        if (field.fieldType === 'relationship') {
          acc[field.id] = camelToSnakeCaseKeys(field.relation!);
        }
        return acc;
      },
      {}
    );

    const fieldValues = Array.from<FieldValue, FieldValueWithRelations>(
      recordFieldsQuery.data ?? [],
      (field) => {
        if (field.field_type === 'relationship') {
          return {
            ...field,
            relation: relations[field.id],
          };
        }

        return field;
      }
    );

    const falsyFieldValues: FieldValueWithRelations[] = [];
    const fieldMap = new Map<string, FieldLike>();

    for (const field of fields) {
      fieldMap.set(field.id, field);

      // Falsey field values are not returned from the API - build field values
      // for field types that should display something when false/blank.
      if (fieldValues.some((fv) => fv.id === field.id)) {
        continue;
      }

      const isReferencedInBlock = mergeFieldNames.includes(field.name);

      if (isReferencedInBlock && field.fieldType === 'checkbox') {
        falsyFieldValues.push({
          display_name: field.displayName,
          id: field.id,
          field_type: field.fieldType,
          name: field.name,
          value: false,
        });
      }
    }

    const lookup = {
      [objectName]: getMergeFieldLookupForFields(
        fieldValues.concat(falsyFieldValues),
        fieldMap,
        t
      ),
      business: getBusinessMergeFieldLookup({
        business: auth.chosenBusiness,
        countries: countriesQuery.data,
        phoneRegions: phoneRegionsQuery.data,
      }),
      business_variable: getBusinessVariablesMergeFieldLookup(
        auth.chosenBusiness.business_variables
      ),
      team_member: getTeamMemberMergeFieldLookup({
        teamMember: auth.teamMember,
        roles: rolesQuery.data,
        phoneRegions: phoneRegionsQuery.data,
        business: auth.chosenBusiness,
      }),
    };

    return (content: string) => {
      return replaceMergeFields(content, lookup);
    };
  }, [
    t,
    isLoading,
    objectName,
    auth,
    fields,
    mergeFieldNames,
    countriesQuery.data,
    phoneRegionsQuery.data,
    rolesQuery.data,
    recordFieldsQuery.data,
  ]);

  return (
    <VerticalTile
      enableBoxShadow={Boolean(blockJson.ROOT.props.hasShadow)}
      forceFixedHeight
      transparent={isTransparent}
      padding={0}
      middle={
        <Panel>
          <StaticBlockWrapper
            height={blockJson.ROOT.props.height}
            isMobile={isMobile}
          >
            {isLoading ? (
              <div
                style={style}
                className="h-[var(--custom-content-block-height)]"
              >
                <Loader loading />
              </div>
            ) : (
              <StaticContentBlock
                borderRadius="8px"
                data={blockJson}
                nodes={nodes}
                applyMobileBreakpoints={isMobile}
                fullHeight={isMobile}
                javascriptActions={javascriptActions}
                textTransform={textTransform}
                entityId={id}
                objectId={objectId}
                onLogActivityButtonClick={(activityId: string) => {
                  openLogActivityModal(activityId);
                }}
                onStartAutomationButtonClick={(automationId: string) => {
                  startAutomation(automationId);
                }}
                handleScriptError={(e: unknown) => {
                  showToast({
                    message: `${t('Script could not be executed')}: ${
                      (e as Error)?.message
                    }`,
                    variant: toastVariant.FAILURE,
                  });
                }}
              />
            )}
          </StaticBlockWrapper>
        </Panel>
      }
    />
  );
};

export const CustomContentBlock = withErrorBoundary(Block);
